import React, { useState } from 'react'
import { useHistory, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Input, message } from 'antd'
import { Mail, Lock, ArrowRight, Eye, EyeOff, Clock, DollarSign } from 'lucide-react'
import { AUTH } from '../../utils/apis'
import { authToken, loginUser, setNotificationFlag } from '../../Redux/actions/authActions'
import './LoginPage.css'
import paths from '../../Config/paths'
import { secureApi } from '../../Config/api'
import { getFcmToken } from '../../Config/firebase'
import { signInWithGoogleCalendar, clearGoogleCalendarAuth } from '../../Config/googleCalendar'

const Login = () => {
  const user = useSelector(state => state.authReducer.user)
  const dispatch = useDispatch()
  const history = useHistory()

  const [passwordVisible, setPasswordVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  if (user) {
    return <Redirect to={paths?.HOME} />
  }

  const handleLogin = async () => {
    try {
      setLoading(true)
      setError('')

      if (!email || !password) {
        message.error('Please provide all fields')
        return
      }

      const fcmToken = await getFcmToken()
      // console.log('FCM Token:', fcmToken)
      const response = await secureApi?.post(AUTH?.LOGIN, { email, password, fcmToken })
      // console.log('Login Response:', response)
      const token = response?.encodedToken
      if (response) {
        const userData = response?.user
        dispatch(loginUser(userData, token))
        dispatch(authToken(token))
        dispatch(setNotificationFlag(true))
        history.push(paths?.HOME)
      }
      if (response?.success === false) {
        message.error(response?.message)
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || 'Something went wrong. Please try again.'
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const forgotPassword = () => {
    history.push(paths?.FORGOT_PASSWORD)
  }

  // Google sign-in: gets an OAuth token from Google (calendar access
  // included), then the backend verifies it and issues the app's own JWT —
  // so this button logs the user fully into the app, same as the form above.
  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true)
      setError('')

      const authData = await signInWithGoogleCalendar()

      const fcmToken = await getFcmToken()
      const response = await secureApi?.post(AUTH?.GOOGLE_LOGIN, {
        accessToken: authData?.accessToken,
        fcmToken,
      })

      if (response?.success === false) {
        // e.g. Google account not registered in the app
        clearGoogleCalendarAuth()
        message.error(response?.message || 'Google sign-in failed. Please try again.')
        return
      }

      const token = response?.encodedToken
      if (response) {
        dispatch(loginUser(response?.user, token))
        dispatch(authToken(token))
        dispatch(setNotificationFlag(true))
        history.push(paths?.HOME)
      }
    } catch (err) {
      if (err?.message === 'popup_closed_by_user') return
      console.error('Google Sign-In Error:', err)
      clearGoogleCalendarAuth()
      message.error(err?.response?.data?.message || 'Google sign-in failed. Please try again.')
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleLogin()
    }
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  return (
    <div className="login-container">
      <div className="left-section">
        <div className="left-content">
          <div className="logo-section">
            <div className="logo-icon">
              <span className="logo-letter">T</span>
            </div>
            <div className="logo-text">
              <div className="company-name">Team</div>
              <div className="company-subtitle">MANAGEMENT</div>
            </div>
          </div>

          <div className="welcome-section">
            <h1 className="main-heading">
              Welcome to <br />
              <span className="dashboard-text">Team Management Dashboard</span>
            </h1>
            <p className="description">
              Track your daily work hours and expenses in one place. <br />
              Simple, efficient and fully managed for employees and admins.
            </p>
          </div>

          <div className="features-section">
            <div className="feature-card">
              <div className="feature-icon analytics-icon">
                <Clock size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Work Hours</h3>
                <p className="feature-description">
                  Log and monitor daily hours
                </p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon security-icon">
                <DollarSign size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Expenses</h3>
                <p className="feature-description">
                  Record travel, meals & extra costs
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="right-section">
        <div className="login-form-container">
          <div className="form-header">
            <h2 className="form-title">Welcome back</h2>
            <p className="form-subtitle">Sign in to access your Team Management Dashboard</p>
          </div>

          <Form layout="vertical" className="login-form" onKeyPress={handleKeyPress}>
            <Form.Item
              label="Email"
              name="email"
              validateTrigger={['onSubmit']}
              rules={[{ required: true, message: 'Please enter your Email' }]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <Input
                  type="email"
                  placeholder="Enter Your Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                />
              </div>
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please Enter Your Password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle"
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </Form.Item>

            <Form.Item>
              <button
                type="button"
                onClick={handleLogin}
                disabled={loading}
                className={`submit-button ${loading ? 'loading' : ''}`}
              >
                {loading ? 'Signing in...' : (
                  <>
                    Sign in
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </Form.Item>
          </Form>

          <div className="remember-me">
            <p className="forgot-password-text" onClick={forgotPassword}>
              Forgot password?
            </p>
          </div>

          <div className="google-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="google-signin-button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
          >
            <GoogleLogo />
            {googleLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>
          <p className="google-hint">
            Use the Google account registered with your team — your calendar events will be available on the dashboard.
          </p>
        </div>
      </div>
    </div>
  )
}

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
)

export default Login